import React from 'react';
import { Platform, View, Text, TextInput, StyleSheet, TextInputProps } from 'react-native';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';

interface AdminFormFieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  labelSuffix?: string;
  children?: React.ReactNode;
  fullWidth?: boolean;
}

export const AdminFormField: React.FC<AdminFormFieldProps> = ({
  label,
  required,
  hint,
  error,
  labelSuffix,
  children,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <View style={styles.group}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, isDark && { color: colors.text.primary }]}>
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Text>
        {labelSuffix ? (
          <Text style={[styles.labelSuffix, isDark && { color: colors.text.disabled }]}>
            {labelSuffix}
          </Text>
        ) : null}
      </View>
      {children}
      {hint && !error ? (
        <Text style={[styles.hint, isDark && { color: colors.text.disabled }]}>{hint}</Text>
      ) : null}
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
};

interface AdminTextInputProps extends TextInputProps {
  readOnly?: boolean;
  hasError?: boolean;
}

export const AdminTextInput: React.FC<AdminTextInputProps> = ({ readOnly, hasError, style, ...props }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <TextInput
      style={[
        styles.input,
        readOnly && styles.inputReadOnly,
        hasError && styles.inputError,
        isDark && {
          backgroundColor: colors.input.background,
          borderColor: colors.input.border,
          color: colors.text.primary,
        },
        isDark && readOnly && {
          backgroundColor: colors.background.elevated,
          color: colors.text.disabled,
        },
        !isDark && {
          backgroundColor: AdminColors.bgSurface,
          borderColor: AdminColors.borderDefault,
          color: AdminColors.textPrimary,
        },
        style,
      ]}
      placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray350}
      editable={!readOnly}
      {...props}
    />
  );
};

const styles = StyleSheet.create({
  group: {
    flexDirection: 'column',
    gap: 5,
    marginBottom: 12,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  required: {
    color: AdminColors.textError,
  },
  labelSuffix: {
    fontSize: 11,
    fontWeight: '400',
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  input: {
    height: 36,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    fontSize: 13,
    color: AdminColors.textPrimary,
    backgroundColor: AdminColors.bgSurface,
    fontFamily: FontFamily.regular,
  },
  inputReadOnly: {
    backgroundColor: AdminColors.nearWhite,
    color: AdminColors.gray400,
  },
  inputError: {
    borderColor: AdminColors.textError,
  },
  hint: {
    fontSize: 11,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 11,
    color: AdminColors.textError,
    fontFamily: FontFamily.regular,
  },
});

export default AdminFormField;
